<template>
	<Col>
		<SyncGroupSuggestion @synced-group-created="onSyncedGroupCreated" class="mb-2" v-if="canManageGroups" :workflowId="workflow.id" />
		<Col class="combobox-container">
			<UserSelectCombobox
				:workflowId="workflow.id"
				ref="user_select_combobox"
				class="mb-2 user-select-combobox"
				:showSuggestions="isPostPublishMode"
				:minimalBillingLevel="minimalBillingLevel"
				:comboboxSelection.sync="localComboboxSelection"
				:includeEveryoneGroup="!isWorkflowSharedWithEveryone"
			>
				<template #right-side-prepend>
					<Row class="flex-align-center pointer mr-n1">
						<AppVMenu open-on-hover min-width="220" max-width="220" rounded top left nudge-top="16" nudge-left="0">
							<template v-slot:activator="{ on, attrs }">
								<div class="flex-align-center display-flex mr-n1 flex-row pointer mr-n1" v-on="on" v-bind="attrs">
									<h3 class="reg can-use">Can use</h3>
									<SIcon name="installation-tab-info" width="12" height="20" />
								</div>
							</template>
							<Col class="tooltip-menu-container pa-2">
								Teammates will gain use access to the feature. Create a project to collaborate
								<a style="color: white" target="_blank" href="https://docs.ply.io/manage-and-collaborate/understand-spaces">learn more</a>
							</Col>
						</AppVMenu>
					</Row>
				</template>
			</UserSelectCombobox>
		</Col>
		<Row v-if="showEmailInvitationDescription" class="flex-align-center justify-between mb-2">
			<Row class="flex-align-center">
				<AppIcon name="email-invitation-chip" class="mr-2" />
				<h5 class="email-invitation-text pt-1">{{ amountOfInvitedMembersText }}</h5>
			</Row>
			<RoleSelector :disabledRoles="disabledRoles" :value="inviteUserRole" :onChange="(role) => $emit('update:inviteUserRole', role)" />
		</Row>
		<Row v-if="showMemberAutoUpgradeIndication" class="flex-align-center justify-between mb-2">
			<Row class="flex-align-center">
				<AppIcon name="auto-member-upgrade" class="mr-2" />
				<h5 class="email-invitation-text pt-1">{{ amountOfAutoUpgradedMembersText }}</h5>
			</Row>
			<h4 @click="removeAutoUpgradedMembers" class="link text-colorText">Remove members</h4>
		</Row>
	</Col>
</template>
<script>
import { AccountPermissions, WorkflowAccessType } from '@ply-io/ply-front-shared-utilities';

import { BILLING_LEVEL_TO_RECOMMENDED_ROLE } from '@/components/Billing/billing-service.const';
import { BillingLevel } from '@/components/Billing/billing-types';
import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import SyncGroupSuggestion from '@/components/dialogs/ShareModuleDialog/SyncGroupSuggestion.vue';
import UserSelectCombobox from '@/components/dialogs/ShareModuleDialog/UserSelectCombobox.vue';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import { AccountRoles, ACCOUNT_ROLES } from '@/utils/account-service.const';
import { pluralize } from '@/utils/language-service';
export default {
	name: 'ShareDialogUserSelectCombobox',
	components: {
		RoleSelector,
		UserSelectCombobox,
		SyncGroupSuggestion,
	},
	props: {
		workflow: { type: Object },
		inviteUserRole: { type: String },
		comboboxSelection: { type: Array },
		isPostPublishMode: { type: Boolean },
		minimalBillingLevel: { type: String },
		usersWithBadBillingLevel: { type: Array },
	},
	computed: {
		...mapGettersShorthand({ hasAccountPermission }),
		showEmailInvitationDescription() {
			return this.usersInvitedByMail.length > 0;
		},
		amountOfMembersToAutoUpgrade() {
			return this.usersWithBadBillingLevel.length;
		},
		disabledRoles() {
			if (this.hasContext) {
				if (BillingLevel.StandardMember) {
					return [AccountRoles.LiteEndUser];
				} else if (BillingLevel.PremiumMember) {
					return [AccountRoles.LiteEndUser, AccountRoles.StandardEndUser];
				}
			}
			return [AccountRoles.LiteEndUser];
		},
		serviceContextSlug() {
			return WorkflowService.getWorkflowContextServiceSlug(this.workflow);
		},
		hasContext() {
			return !!this.serviceContextSlug;
		},
		showMemberAutoUpgradeIndication() {
			return this.hasContext && this.amountOfMembersToAutoUpgrade > 0;
		},
		amountOfAutoUpgradedMembersText() {
			return `${pluralize('member', this.amountOfMembersToAutoUpgrade, true)} will be upgraded to ${
				ACCOUNT_ROLES[BILLING_LEVEL_TO_RECOMMENDED_ROLE[this.minimalBillingLevel]]
			} to use this feature`;
		},
		usersInvitedByMail() {
			return this.localComboboxSelection.filter((selection) => this.isMailSelection(selection));
		},
		amountOfInvitedMembersText() {
			return `${pluralize('member', this.usersInvitedByMail.length, true)} will be invited to Ply, choose their workspace role:`;
		},
		canManageGroups() {
			return this.hasAccountPermission(AccountPermissions.ManageGroups);
		},
		isWorkflowSharedWithEveryone() {
			return this.workflow.access_type === WorkflowAccessType.WorkspaceUsers;
		},
		localComboboxSelection: {
			get() {
				return this.comboboxSelection;
			},
			set(...args) {
				this.$emit('update:comboboxSelection', ...args);
			},
		},
	},
	methods: {
		onSyncedGroupCreated(groupId) {
			this.$refs.user_select_combobox.addItemToSelection(groupId);
		},
		removeAutoUpgradedMembers() {
			this.$emit('remove-auto-upgraded-members');
		},
		isMailSelection(selection) {
			return typeof selection === 'string' || selection.suggestion;
		},
	},
};
</script>
<style scoped lang="scss">
.can-use {
	opacity: 0.5;
	color: #292829;
	letter-spacing: 0px;
}
.combobox-container {
	height: 100% !important;
	min-height: 40px !important;
	max-height: 360px !important;
}
.user-select-combobox {
	::v-deep {
		.app-combobox .v-select__selections {
			padding: 2px 57px 2px 0px !important;
		}
		.v-input__slot,
		.v-input__control,
		.text-field--outlined.v-input--dense.v-text-field--outlined {
			@extend .combobox-container;
		}
		.v-select__selections {
			top: unset !important;
			width: unset !important;
			position: unset !important;
			padding-right: unset !important;
		}
	}
}

.email-invitation-text {
	font-size: 11px;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
.tooltip-menu-container {
	width: 220px;
	font-size: 12px;
	color: var(--white);
	border-radius: 5px;
	letter-spacing: 0px;
	font-weight: normal;
	background: #262626;
	box-shadow: 0px 3px 6px #00000029;
}
</style>
